"use client";

import React, { useState, useEffect } from "react";
import { ArrowRight, Sparkles, Layers, Cpu, Users, Calendar, BookOpen, Compass, PenTool, Code, FlaskConical, Wrench, Trophy, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare const wvcClient: any;
const getWvcClient = () =>
  typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;

function useRobotoLang(): "vi" | "en" {
  const [lang, setLang] = useState<"vi" | "en">(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("roboto_lang");
      if (saved === "en" || saved === "vi") return saved;
    }
    return "vi";
  });

  useEffect(() => {
    const handleLangChange = (e: any) => {
      const newLang = e?.detail || localStorage.getItem("roboto_lang") || "vi";
      setLang(newLang === "en" ? "en" : "vi");
    };
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "roboto_lang") {
        setLang(e.newValue === "en" ? "en" : "vi");
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  return lang;
}

interface StageDefinition {
  num: string;
  fieldKey: string;
  vietnameseTitle: string;
  englishLabel: string;
  icon: React.ComponentType<{ className?: string }>;
}

const LIFECYCLE_STAGES: StageDefinition[] = [
  {
    num: "01",
    fieldKey: "challenge",
    vietnameseTitle: "Thử thách",
    englishLabel: "Challenge",
    icon: Compass,
  },
  {
    num: "02",
    fieldKey: "idea",
    vietnameseTitle: "Ý tưởng",
    englishLabel: "Idea & Formulation",
    icon: Sparkles,
  },
  {
    num: "03",
    fieldKey: "design",
    vietnameseTitle: "Thiết kế",
    englishLabel: "Design & Architecture",
    icon: PenTool,
  },
  {
    num: "04",
    fieldKey: "programming",
    vietnameseTitle: "Lập trình",
    englishLabel: "Programming & Logic",
    icon: Code,
  },
  {
    num: "05",
    fieldKey: "testing",
    vietnameseTitle: "Thử nghiệm",
    englishLabel: "Testing & Validation",
    icon: FlaskConical,
  },
  {
    num: "06",
    fieldKey: "improvement",
    vietnameseTitle: "Cải tiến",
    englishLabel: "Iteration & Refinement",
    icon: Wrench,
  },
  {
    num: "07",
    fieldKey: "result",
    vietnameseTitle: "Kết quả",
    englishLabel: "Final Result & Impact",
    icon: Trophy,
  },
  {
    num: "08",
    fieldKey: "skills_developed",
    vietnameseTitle: "Kỹ năng",
    englishLabel: "Skills & Capabilities",
    icon: CheckCircle2,
  },
];

function RelatedProjectsConsumer({ currentProjectId }: { currentProjectId: string }) {
  const { posts, loading } = usePosts();
  const lang = useRobotoLang();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            data-index={i}
            className="h-64 rounded-xl bg-muted/40 animate-pulse border border-border"
          />
        ))}
      </div>
    );
  }

  const filteredPosts = posts.filter((p) => p.id !== currentProjectId).slice(0, 3);

  if (filteredPosts.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-8 text-center text-muted-foreground">
        <p className="text-sm">
          {lang === "vi"
            ? "Nhiều dự án kỹ thuật của học viên sẽ sớm được giới thiệu tại đây."
            : "More student engineering projects will be showcased here soon."}
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {filteredPosts.map((project, i) => {
        const custom = project.customFields || {};
        const title = custom.project_name || project.title || (lang === "vi" ? "Dự án Học viên" : "Student Build");
        const domain = custom.field_domain || "Robotics";
        const age = custom.age_range || (lang === "vi" ? "8–12 tuổi" : "8–12 yrs");
        const customImg = custom.image
          ? { src: custom.image, srcset: "", sizes: "" }
          : null;
        const projectThumb = project.featuredImage || customImg?.src || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/hero_image.png";

        return (
          <Card
            key={project.id}
            data-index={i}
            className="overflow-hidden border border-border bg-card transition-[transform,border-color,box-shadow] duration-200 hover:-translate-y-1 hover:border-secondary hover:shadow-md flex flex-col justify-between"
          >
            <div className="relative aspect-video w-full overflow-hidden bg-muted">
              {project.featuredImage ? (
                <img
                  src={projectThumb}
                  alt={title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                />
              ) : customImg?.src ? (
                <img
                  src={customImg.src}
                  data-wvc-srcset={customImg.srcset}
                  data-wvc-sizes={customImg.sizes}
                  alt={title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                />
              ) : (
                <img
                  src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/hero_image.png"
                  data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/hero_image.png-wvc-srcset"
                  data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/hero_image.png-wvc-sizes"
                  alt={title}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              )}
              <div className="absolute top-3 left-3 flex gap-2">
                <Badge variant="secondary" className="text-2xs font-semibold uppercase tracking-wider">
                  {domain}
                </Badge>
              </div>
            </div>

            <CardContent className="p-5 flex-1 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                  <span>{lang === "vi" ? "Độ tuổi:" : "Age:"} {age}</span>
                  {custom.technology && (
                    <span className="truncate max-w-[120px] font-mono">{custom.technology}</span>
                  )}
                </div>
                <h4 className="font-serif text-lg font-bold text-foreground line-clamp-2 mb-2">
                  {title}
                </h4>
                {custom.short_description && (
                  <p className="text-xs text-muted-foreground line-clamp-2 mb-4 leading-relaxed">
                    {custom.short_description}
                  </p>
                )}
              </div>

              <div className="pt-2">
                <Button asChild variant="outline" size="sm" className="w-full text-xs font-semibold">
                  <Link to={project.link || "/ProjectsCompetitionsPage"}>
                    {lang === "vi" ? "Xem dự án" : "View Project"}
                    <ArrowRight className="size-3 ml-1" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function SingleProjectConsumer() {
  const { posts, loading, error, refetch } = usePosts();
  const lang = useRobotoLang();

  if (loading) {
    return (
      <div className="w-full min-h-[500px] flex items-center justify-center p-8">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="size-12 rounded-full border-4 border-primary border-t-transparent animate-spin" />
          <p className="text-sm font-medium text-muted-foreground">
            {lang === "vi" ? "Đang tải bản thiết kế dự án..." : "Loading project blueprint..."}
          </p>
        </div>
      </div>
    );
  }

  if (error || posts.length === 0) {
    return (
      <div className="max-w-3xl mx-auto py-20 px-6 text-center">
        <div className="rounded-xl border border-border bg-card p-10 shadow-sm">
          <p className="text-lg font-serif font-bold text-foreground mb-2">
            {lang === "vi" ? "Dự án hiện chưa khả dụng" : "Project not currently available"}
          </p>
          <p className="text-sm text-muted-foreground mb-6">
            {lang === "vi"
              ? "Chi tiết mô hình robot được yêu cầu không thể tải hoặc đang được cập nhật."
              : "The requested robotics build details could not be loaded or is being updated."}
          </p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={() => refetch()}>
              {lang === "vi" ? "Thử lại" : "Try again"}
            </Button>
            <Button asChild>
              <Link to="/ProjectsCompetitionsPage">
                {lang === "vi" ? "Duyệt tất cả dự án" : "Browse all builds"}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const post = posts[0];
  const custom = post.customFields || {};

  const projectName = custom.project_name || post.title || (lang === "vi" ? "Hệ thống Robotics Nâng cao" : "Advanced Robotics System");
  const ageRange = custom.age_range || (lang === "vi" ? "9–14 tuổi" : "9–14 years");
  const fieldDomain = custom.field_domain || "Robotics";
  const technology = custom.technology || "LEGO Spike Prime / Python";
  const shortDescription = custom.short_description || post.excerpt || "";

  // Prepare custom image or featured image
  const customImg = custom.image ? { src: custom.image, srcset: "", sizes: "" } : null;
  const heroImageSrc = post.featuredImage || customImg?.src || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/hero_image.png";

  // Parse skill tags
  const skillsList = custom.skills
    ? custom.skills.split(",").map((s: string) => s.trim()).filter(Boolean)
    : ["Mechanical Assembly", "Algorithmic Logic", "Sensor Calibration"];

  const skillsDevelopedList = custom.skills_developed
    ? custom.skills_developed.split(",").map((s: string) => s.trim()).filter(Boolean)
    : ["Iterative Debugging", "Hardware Resilience", "Autonomous Navigation"];

  return (
    <article className="w-full">
      {/* 1. Full-width Hero Banner with Overlay */}
      <header className="relative w-full min-h-[460px] md:min-h-[540px] flex items-end overflow-hidden bg-foreground">
        <div className="absolute inset-0 z-0">
          {post.featuredImage ? (
            <img
              src={heroImageSrc}
              alt={projectName}
              loading="lazy"
              className="h-full w-full object-cover object-center"
            />
          ) : customImg?.src ? (
            <img
              src={customImg.src}
              data-wvc-srcset={customImg.srcset}
              data-wvc-sizes={customImg.sizes}
              alt={projectName}
              loading="lazy"
              className="h-full w-full object-cover object-center"
            />
          ) : (
            <img
              src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/hero_image.png"
              data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/hero_image.png-wvc-srcset"
              data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/hero_image.png-wvc-sizes"
              alt={projectName}
              loading="lazy"
              className="h-full w-full object-cover object-center"
            />
          )}
          {/* Engineering Scrim Overlay for WCAG AA readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/30" />
        </div>

        {/* Blueprint Spec Rail accent line */}
        <div className="absolute top-0 inset-x-0 h-1 bg-secondary z-10" />

        <div className="relative z-10 max-w-6xl mx-auto px-6 py-12 md:py-16 w-full text-white">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-secondary text-secondary-foreground uppercase tracking-wider">
              {fieldDomain}
            </span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-white/20 text-white backdrop-blur-xs border border-white/20">
              {lang === "vi" ? "Độ tuổi:" : "Age:"} {ageRange}
            </span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-mono font-medium bg-primary/90 text-primary-foreground border border-white/10">
              {technology}
            </span>
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-4 max-w-4xl leading-tight">
            {projectName}
          </h1>

          {shortDescription && (
            <p className="text-base sm:text-lg md:text-xl text-white/90 max-w-3xl leading-relaxed font-sans">
              {shortDescription}
            </p>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-4 text-xs font-medium text-white/80 border-t border-white/20 pt-4">
            <span className="flex items-center gap-1.5">
              <Layers className="size-4 text-secondary" />
              {lang === "vi" ? "Dự án Phòng thí nghiệm Học viên" : "Student Laboratory Project"}
            </span>
            <span className="hidden sm:inline opacity-50">•</span>
            <span className="flex items-center gap-1.5">
              <Cpu className="size-4 text-secondary" />
              {lang === "vi" ? "Tích hợp Phần cứng & Phần mềm" : "Hardware & Software Integration"}
            </span>
            <span className="hidden sm:inline opacity-50">•</span>
            <span className="flex items-center gap-1.5">
              <Users className="size-4 text-secondary" />
              {lang === "vi" ? "Lộ trình Đổi mới ROBOTO Academy" : "ROBOTO Academy Innovation Track"}
            </span>
          </div>
        </div>
      </header>

      {/* 2. Project Lifecycle Stages */}
      <div className="max-w-5xl mx-auto px-6 py-16 md:py-24">
        <div className="mb-12">
          <div className="flex items-center gap-2 mb-2">
            <span className="h-2 w-2 rounded-xs bg-secondary" />
            <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground font-sans">
              {lang === "vi" ? "Hồ sơ Vòng đời Dự án" : "Lifecycle Documentation"}
            </span>
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">
            {lang === "vi" ? "Hành trình kỹ thuật dự án" : "Project engineering journey"}
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground mt-2 max-w-2xl leading-relaxed">
            {lang === "vi"
              ? "Theo dõi toàn bộ quy trình từng bước các kỹ sư nhí thực hiện, từ định hình bài toán ban đầu đến kiểm thử hiệu năng thực tế."
              : "Follow the complete step-by-step process our student developers executed, from initial constraint framing to final performance validation."}
          </p>
        </div>

        <div className="space-y-12">
          {LIFECYCLE_STAGES.map((stage, idx) => {
            const content = custom[stage.fieldKey];
            const IconComp = stage.icon;
            const isSkillsStage = stage.fieldKey === "skills_developed";

            return (
              <section
                key={stage.fieldKey}
                data-index={idx}
                className="relative pl-6 sm:pl-10 border-l-2 border-border transition-colors hover:border-secondary group"
              >
                {/* Stage marker on timeline */}
                <div className="absolute -left-[17px] top-0 flex size-8 items-center justify-center rounded-full bg-card border-2 border-border text-foreground font-mono text-xs font-bold group-hover:border-secondary group-hover:bg-secondary group-hover:text-secondary-foreground transition-colors shadow-2xs">
                  {stage.num}
                </div>

                <div className="rounded-xl border border-border bg-card p-6 sm:p-8 shadow-xs">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-4 pb-3 border-b border-border/60">
                    <div className="flex items-center gap-3">
                      <div className="size-9 rounded-lg bg-muted flex items-center justify-center text-primary">
                        <IconComp className="size-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-serif text-xl sm:text-2xl font-bold text-foreground">
                            {lang === "vi" ? stage.vietnameseTitle : stage.englishLabel}
                          </span>
                        </div>
                      </div>
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground">
                      {lang === "vi" ? `Giai đoạn ${stage.num}/08` : `Stage ${stage.num} of 08`}
                    </span>
                  </div>

                  {isSkillsStage ? (
                    <div className="space-y-5">
                      <div className="flex flex-wrap gap-2">
                        {skillsDevelopedList.map((skill: string, index: number) => (
                          <span
                            key={index}
                            data-index={index}
                            className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-secondary/15 text-secondary-foreground border border-secondary/30"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>

                      <div className="pt-4 border-t border-border/60">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="font-serif text-base font-bold text-foreground">
                            {lang === "vi" ? "Kỹ thuật & Công cụ" : "Technical & Core Skills"}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {skillsList.map((skill: string, index: number) => (
                            <span
                              key={index}
                              data-index={index}
                              className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-muted text-foreground border border-border"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : content ? (
                    <div className="text-sm sm:text-base text-foreground/90 whitespace-pre-wrap leading-relaxed font-sans">
                      {content}
                    </div>
                  ) : (
                    <p className="text-sm italic text-muted-foreground">
                      {lang === "vi"
                        ? `Thông số kỹ thuật và ghi chú chi tiết cho giai đoạn ${stage.vietnameseTitle.toLowerCase()} được lưu trữ trong nhật ký phòng lab học viện.`
                        : `Detailed telemetry and notes for the ${stage.englishLabel.toLowerCase()} phase are recorded in the academy laboratory log.`}
                    </p>
                  )}
                </div>
              </section>
            );
          })}
        </div>

        {/* 3. Related Projects Section */}
        <section className="mt-20">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="h-2 w-2 rounded-xs bg-secondary" />
                <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  {lang === "vi" ? "Hồ sơ Năng lực Học viên" : "Student Portfolios"}
                </span>
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground">
                {lang === "vi" ? "Dự án kỹ thuật liên quan" : "Related engineering projects"}
              </h3>
            </div>
            <Button asChild variant="outline" size="sm">
              <Link to="/ProjectsCompetitionsPage">
                {lang === "vi" ? "Xem tất cả dự án" : "View all projects"}
                <ArrowRight className="size-4 ml-1.5" />
              </Link>
            </Button>
          </div>

          <PostsProvider wp_query={{ post_type: "project", per_page: 4, status: ["publish"] }}>
            <RelatedProjectsConsumer currentProjectId={post.id} />
          </PostsProvider>
        </section>

        {/* 4. Prominent Call-to-Action */}
        <div className="mt-20 rounded-2xl bg-primary text-primary-foreground p-8 sm:p-12 text-center relative overflow-hidden shadow-lg">
          {/* Subtle laboratory grid accent pattern */}
          <div 
            className="absolute inset-0 opacity-10 pointer-events-none"
            style={{
              backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)",
              backgroundSize: "24px 24px"
            }} 
          />
          
          <div className="relative z-10 max-w-2xl mx-auto space-y-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-secondary text-secondary-foreground uppercase tracking-widest">
              {lang === "vi" ? "Sẵn sàng để con tự tay chế tạo?" : "Ready for your child to build?"}
            </span>
            <h3 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold text-white tracking-tight leading-tight">
              {lang === "vi" ? "Khám phá các chương trình học liên quan" : "Explore related learning programs"}
            </h3>
            <p className="text-sm sm:text-base text-white/90 leading-relaxed font-sans max-w-xl mx-auto">
              {lang === "vi"
                ? "Lộ trình bài bản giúp học viên nhí phát triển từ lắp ráp cơ khí căn bản đến robot thi đấu quốc tế, lập trình Python và sáng tạo độc lập."
                : "Our structured curriculum guides young students from basic mechanical builds to competitive robotics, Python programming, and independent invention."}
            </p>
            <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                asChild
                size="lg"
                className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90 font-semibold px-8 shadow-md"
              >
                <Link to="/ProgramsPage">
                  {lang === "vi" ? "Khám phá Lộ trình Học" : "Explore Learning Tracks"}
                  <ArrowRight className="size-4 ml-2" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="w-full sm:w-auto border-white/40 bg-white/10 text-white hover:bg-white/20 hover:text-white"
              >
                <Link to="/FreeTrialContactPage">
                  {lang === "vi" ? "Đăng ký Trải nghiệm Miễn phí" : "Book a Free Trial Session"}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function ProjectDetail() {
  const currentWPQueryParams =
    typeof wvcClient !== "undefined" && wvcClient.getCurrentWPQueryParams
      ? wvcClient.getCurrentWPQueryParams()
      : { post_type: "project" };

  const singlePostQuery = new WP_Query(currentWPQueryParams);

  return (
    <section
      id="single-project-template-section"
      data-nav="light"
      className="w-full bg-background text-foreground transition-colors overflow-hidden"
    >
      <PostsProvider wp_query={singlePostQuery}>
        <SingleProjectConsumer />
      </PostsProvider>
    </section>
  );
}